// app.tsx — shell: top app bar + slide-in drawer nav, hash-routed main content. Gated by
// a session check on boot: GET /api/session decides whether the login view or the app
// shell renders. When server-side auth is disabled, /api/session always reports
// authenticated:true, so this gate is a no-op in that mode (see lib/api.ts).
//
// Scenario state, auto-computed run results and app-level settings are all owned here
// rather than by CockpitView, because VergleichView (#vergleich) and EinstellungenView
// (#einstellungen) need to read/write the same state without re-fetching or re-running
// anything — hash routing unmounts the previous view, so anything that must survive a
// route switch has to live above the router.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { ApiError, getSession, postBacktest, postForecast, setUnauthorizedHandler } from './lib/api.js';
import { NAV_ITEMS, useHashRoute } from './lib/router.js';
import type { View } from './lib/router.js';
import { hashScenarioConfig } from './lib/autoCompute.js';
import { buildBacktestRequestBody, buildForecastConfig } from './lib/scenario.js';
import type { RunResult, ScenarioState } from './lib/scenario.js';
import { loadSettings, saveSettings } from './lib/settings.js';
import type { AppSettings } from './lib/settings.js';
import { applyAccentPreset } from './lib/theme.js';
import { defaultRealTermsSettings } from './lib/realTerms.js';
import type { RealTermsSettings } from './lib/realTerms.js';
import {
  addScenario,
  addScenarioFrom,
  duplicateScenario,
  loadPersistedState,
  removeScenario,
  renameScenario,
  resetScenario,
  savePersistedState,
  setActiveScenario,
  updateScenarioInList,
} from './lib/storage.js';
import type { PersistedState } from './lib/storage.js';
import { BurgerIcon, CloseIcon, ExplainIcon, NavIcon } from './components/icons.js';
import { ExplainProvider } from './components/Hint.js';
import { CockpitView } from './views/CockpitView.js';
import { VergleichView } from './views/VergleichView.js';
import { SzenarienView } from './views/SzenarienView.js';
import { HistorieView } from './views/HistorieView.js';
import { EinstellungenView } from './views/EinstellungenView.js';
import { InfoView } from './views/InfoView.js';
import { DatenView } from './views/DatenView.js';
import { LoginView } from './views/LoginView.js';
import { clearShareHash, decodeScenarioFromShareString, extractShareHash } from './lib/share.js';
import type { ScenarioImportResult } from './lib/scenarioIO.js';

type AuthState = 'loading' | 'login' | 'authenticated';

/** Renders NAV_ITEMS (lib/router.ts) as a flat list of nav buttons — shared by the
 * mobile drawer and the desktop sidebar (app.css / global.css toggles which of the two
 * containers is actually visible via `@media (min-width: 1024px)`) so there is exactly
 * ONE place that maps the nav array to markup, per the router's own header comment.
 * `firstItemRef` is drawer-only (see the drawer's focus-trap-lite effect below); the
 * sidebar is always visible, so it has no equivalent open-focus behaviour to wire up. */
/** The Erklärungen (document-by-design) toggle — one shared boolean (AppSettings.
 * showExplanations), three discoverable places to flip it: the app bar (mobile/tablet),
 * the sidebar (desktop) and the drawer header, all rendering this same button. A fourth,
 * labelled entry point lives in Einstellungen -> Darstellung (EinstellungenView.tsx) for
 * anyone who'd rather find it as a persistent setting than an icon. */
function ExplainToggleButton({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      class="explain-toggle-btn"
      aria-pressed={active}
      aria-label={active ? 'Erklärungen ausblenden' : 'Erklärungen einblenden'}
      title={active ? 'Erklärungen ausblenden' : 'Erklärungen einblenden'}
      onClick={onToggle}
    >
      <ExplainIcon />
    </button>
  );
}

function NavButtons({
  activeView,
  onSelect,
  firstItemRef,
}: {
  activeView: View;
  onSelect: (view: View) => void;
  firstItemRef?: RefObject<HTMLButtonElement>;
}) {
  return (
    <>
      {NAV_ITEMS.map((item, i) => (
        <button
          key={item.hash}
          type="button"
          class="nav-item"
          ref={i === 0 && firstItemRef ? firstItemRef : null}
          aria-current={activeView === item.hash ? 'page' : undefined}
          onClick={() => onSelect(item.hash)}
        >
          <NavIcon name={item.icon} />
          {item.label}
        </button>
      ))}
    </>
  );
}

export function App() {
  const [view, navigate] = useHashRoute();
  const [auth, setAuth] = useState<AuthState>('loading');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const firstNavRef = useRef<HTMLButtonElement>(null);

  const [settings, setSettingsState] = useState<AppSettings>(() => loadSettings());
  const [scenarioState, setScenarioState] = useState<PersistedState>(() => loadPersistedState(settings));
  // Keyed by scenario id (lib/storage.ts's ScenarioListEntry.id), not a fixed A/B pair —
  // the N-scenario model. A missing key reads as IDLE_RUN_RESULT everywhere (see the
  // `results[id] ?? IDLE_RUN_RESULT` lookups in CockpitView/VergleichView) rather than
  // needing to be pre-seeded for every scenario up front.
  //
  // AUTO-COMPUTE (user feedback: "berechne immer alle Szenarien ... ich will immer beide
  // sehen"): every scenario in scenarioState.scenarios gets an entry here automatically —
  // there is no manual "Berechnen" trigger anymore. The auto-compute effect below keeps
  // this in sync, memoized by lib/autoCompute.ts's hashScenarioConfig so an UNCHANGED
  // scenario's config is never redundantly recomputed. Monte-Carlo is the one deliberate
  // EXCEPTION — MonteCarloSection.tsx runs it behind its own toggle, entirely independent
  // of this map, per the task's explicit "stays behind its existing toggle" carve-out.
  const [results, setResults] = useState<Record<string, RunResult>>({});
  // lastComputedHash: the hash (see hashScenarioConfig) that produced — or is currently
  // being computed for — each scenario's `results[id]` entry. Compared against each
  // scenario's CURRENT hash on every scenarioState.scenarios change: equal means "inputs
  // unchanged, cache hit, skip" (switching the active tab, editing a DIFFERENT scenario,
  // toggling realTerms/settings, etc. never touches this at all — the effect's own
  // dependency is scenarioState.scenarios, whose reference is stable unless some
  // scenario's own config changed); different means "(re)compute", debounced below.
  // Refs (not state) — purely internal bookkeeping the auto-compute effect owns; changing
  // them must never itself trigger a re-render.
  const lastComputedHashRef = useRef<Record<string, string>>({});
  const debounceTimerRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // Bumped on every computeScenario() call for a given id; a resolved fetch that no
  // longer matches the CURRENT sequence number for its id is stale (a newer edit already
  // superseded it) and is silently dropped instead of clobbering a more recent result —
  // the same "cancelled" idea CockpitView's own fund-fetch effect and MonteCarloSection
  // use, generalized to survive across separate debounced calls rather than one effect's
  // single cleanup closure.
  const requestSeqRef = useRef<Record<string, number>>({});
  // Inflationsbereinigung (Feature 2): session-only (NOT persisted to localStorage, per
  // spec) — lifted here (rather than owned by CockpitView/VergleichView individually) so
  // toggling it in one view is reflected in the other, same pattern as scenarioState/
  // results. Seeded once from Einstellungen's default rate; the toggle itself starts off.
  const [realTerms, setRealTerms] = useState<RealTermsSettings>(() =>
    defaultRealTermsSettings(settings.inflationPercent),
  );

  // Szenario-Bibliothek: shareable-link detection (Feature 3). Parsed ONCE on boot,
  // straight off the raw location.hash (never through useHashRoute/parseHash — a
  // `#share=...` hash is a distinct format from the app's own `#/<view>` route hashes,
  // see lib/share.ts's header comment). A decoded scenario is only ever OFFERED via the
  // banner below — never auto-loaded into A or B — so opening a link can't silently
  // clobber work already sitting in a slot.
  const [pendingShare, setPendingShare] = useState<{ scenario: ScenarioImportResult } | null>(null);

  useEffect(() => {
    const encoded = typeof location !== 'undefined' ? extractShareHash(location.hash) : null;
    if (!encoded) return;
    const result = decodeScenarioFromShareString(encoded);
    setPendingShare({ scenario: result });
  }, []);

  function dismissPendingShare() {
    setPendingShare(null);
    clearShareHash();
  }

  function loadPendingShareInto(id: string) {
    if (pendingShare?.scenario.ok) {
      const scenario = pendingShare.scenario.scenario;
      updateScenario(id, scenario);
      setActiveScenarioAction(id);
    }
    dismissPendingShare();
  }

  function loadPendingShareAsNew() {
    if (pendingShare?.scenario.ok) {
      const scenario = pendingShare.scenario.scenario;
      setScenarioState((s) => addScenarioFrom(s, scenario));
    }
    dismissPendingShare();
  }

  useEffect(() => {
    setUnauthorizedHandler(() => setAuth('login'));
    getSession()
      .then((session) => setAuth(session.authenticated ? 'authenticated' : 'login'))
      .catch(() => setAuth('login'));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    savePersistedState(scenarioState);
  }, [scenarioState]);

  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Re-applies the CSS custom properties whenever the chosen preset changes (Einstellungen
  // -> Darstellung). Boot-time application (before this even mounts, no flash) happens in
  // index.html's inline script — see lib/theme.ts's header comment; this effect is what
  // keeps subsequent in-app changes live without a reload.
  useEffect(() => {
    applyAccentPreset(settings.accentPreset);
  }, [settings.accentPreset]);

  // Focus-trap-lite: focus the first drawer item on open, return focus to the burger on
  // close; Escape closes. No full cyclic tab-trap — the drawer is a short, single-column
  // list, and forcing tab focus back into it on every boundary would be more surprising
  // than helpful for a "-lite" implementation.
  useEffect(() => {
    if (!drawerOpen) return;
    firstNavRef.current?.focus();
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') closeDrawer();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen]);

  function closeDrawer() {
    setDrawerOpen(false);
    burgerRef.current?.focus();
  }

  function go(next: View) {
    navigate(next);
    setDrawerOpen(false);
  }

  function setSettings(next: AppSettings) {
    setSettingsState(next);
  }

  function toggleExplanations() {
    setSettingsState((s) => ({ ...s, showExplanations: !s.showExplanations }));
  }

  function updateScenario(id: string, scenario: ScenarioState) {
    setScenarioState((s) => updateScenarioInList(s, id, scenario));
  }

  function setActiveScenarioAction(id: string) {
    setScenarioState((s) => setActiveScenario(s, id));
  }

  function addScenarioAction() {
    setScenarioState((s) => addScenario(s, settings));
  }

  function duplicateScenarioAction(id: string) {
    setScenarioState((s) => duplicateScenario(s, id));
  }

  /** Drops results for scenario ids that no longer exist (deleted, or replaced by the
   * "last one recreates a fresh default" fallback) — otherwise an orphaned RunResult would
   * just sit unused in the map forever. */
  function pruneResults(current: Record<string, RunResult>, keepIds: string[]): Record<string, RunResult> {
    const keep = new Set(keepIds);
    const next: Record<string, RunResult> = {};
    for (const [id, r] of Object.entries(current)) {
      if (keep.has(id)) next[id] = r;
    }
    return next;
  }

  function removeScenarioAction(id: string) {
    setScenarioState((s) => {
      const next = removeScenario(s, id, settings);
      setResults((r) => pruneResults(r, next.scenarios.map((e) => e.id)));
      return next;
    });
  }

  function renameScenarioAction(id: string, label: string) {
    setScenarioState((s) => renameScenario(s, id, label));
  }

  /** Reset restores BOTH the scenario's factory-default parameters AND its default name
   * (lib/storage.ts's resetScenario — see its own header comment for why the name is no
   * longer kept). No manual results reset needed: changing the scenario's config changes
   * its hash, which the auto-compute effect below picks up on its own next pass, same as
   * any other edit — the previous (pre-reset) result stays visible with a "wird
   * aktualisiert…" indicator for the ~200ms debounce + request round-trip, exactly like
   * every other config change. */
  function resetScenarioAction(id: string) {
    setScenarioState((s) => resetScenario(s, id, settings));
  }

  /** Computes ONE scenario's result and writes it into `results`. Marks the outcome
   * `pending: true` mid-flight (keeping whatever was already showing) when a result
   * already exists for this id, or goes through the full `status: 'loading'` transition
   * when this is the scenario's first-ever compute (nothing previous to keep showing) —
   * see RunResult.pending's own doc comment. A stale response (superseded by a newer call
   * for the same id before this one resolved — requestSeqRef) is silently dropped rather
   * than clobbering a more recent result. Errors (network offline, backtest 409 "keine
   * Daten", missing fund, …) land in THIS scenario's own result only — never thrown
   * further, so one scenario failing can't take any other scenario down with it. */
  async function computeScenario(id: string, scenario: ScenarioState): Promise<void> {
    const seq = (requestSeqRef.current[id] = (requestSeqRef.current[id] ?? 0) + 1);
    const isStale = () => requestSeqRef.current[id] !== seq;

    setResults((r) => {
      const prev = r[id];
      return prev && prev.status !== 'idle' ? { ...r, [id]: { ...prev, pending: true } } : { ...r, [id]: { status: 'loading' } };
    });

    try {
      if (scenario.mode === 'forecast') {
        const config = buildForecastConfig(scenario.forecast);
        const result = await postForecast(config);
        if (isStale()) return;
        setResults((r) => ({ ...r, [id]: { status: 'success', result, mode: 'forecast', config, pending: false } }));
      } else {
        if (!scenario.backtest.fundIsin) {
          if (isStale()) return;
          setResults((r) => ({ ...r, [id]: { status: 'error', error: 'Bitte zuerst einen Fonds auswählen.' } }));
          return;
        }
        const body = buildBacktestRequestBody(scenario.backtest, scenario.backtest.fundIsin);
        const { result, meta } = await postBacktest(body);
        if (isStale()) return;
        setResults((r) => ({ ...r, [id]: { status: 'success', result, meta, mode: 'backtest', pending: false } }));
      }
    } catch (err) {
      if (isStale()) return;
      const message = err instanceof ApiError ? err.message : 'Unbekannter Fehler.';
      setResults((r) => ({ ...r, [id]: { status: 'error', error: message } }));
    }
  }

  /** The auto-compute effect: on every change to scenarioState.scenarios (i.e. some
   * scenario's own config actually changed — see the ref's own doc comment above for why
   * unrelated state changes never re-trigger this), (re)computes every scenario whose
   * current hashScenarioConfig no longer matches the hash that produced its existing
   * result, each independently debounced ~200ms so a slider drag's many rapid onChange
   * calls collapse into one recompute instead of one per tick. Forecast is fast/synchronous
   * server-side but goes through the exact same debounced path as backtest — one code path,
   * not two, and 200ms is imperceptible either way. Monte-Carlo is NOT part of this: it
   * stays behind its own toggle in MonteCarloSection.tsx, untouched by this effect. */
  useEffect(() => {
    const currentIds = new Set(scenarioState.scenarios.map((e) => e.id));
    for (const id of Object.keys(debounceTimerRef.current)) {
      if (!currentIds.has(id)) {
        clearTimeout(debounceTimerRef.current[id]);
        delete debounceTimerRef.current[id];
        delete lastComputedHashRef.current[id];
        delete requestSeqRef.current[id];
      }
    }
    for (const entry of scenarioState.scenarios) {
      const hash = hashScenarioConfig(entry.scenario);
      if (lastComputedHashRef.current[entry.id] === hash) continue; // cache hit — unchanged inputs, skip
      const existingTimer = debounceTimerRef.current[entry.id];
      if (existingTimer) clearTimeout(existingTimer);
      debounceTimerRef.current[entry.id] = setTimeout(() => {
        lastComputedHashRef.current[entry.id] = hash;
        void computeScenario(entry.id, entry.scenario);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioState.scenarios]);

  /** Bypasses the debounce + hash cache and recomputes `id` immediately — the error
   * banner's "Erneut versuchen" fallback (Cockpit and Vergleich both use this), for a
   * failure no config change would otherwise re-trigger (e.g. a transient offline error,
   * or a backtest 409 that would now succeed because the required data was refreshed
   * elsewhere in the app). */
  function forceRecomputeScenario(id: string): void {
    const entry = scenarioState.scenarios.find((e) => e.id === id);
    if (!entry) return;
    const existingTimer = debounceTimerRef.current[id];
    if (existingTimer) {
      clearTimeout(existingTimer);
      delete debounceTimerRef.current[id];
    }
    lastComputedHashRef.current[id] = hashScenarioConfig(entry.scenario);
    void computeScenario(id, entry.scenario);
  }

  if (auth === 'loading') {
    // No app bar mounted yet at this point, so this brief pre-boot state needs its own
    // top safe-area padding (the app bar normally carries that).
    return (
      <div class="empty-state" style={{ paddingTop: 'calc(1.5rem + var(--safe-top))' }}>
        <span class="spinner" /> Wird geladen…
      </div>
    );
  }

  if (auth === 'login') {
    return <LoginView onLoggedIn={() => setAuth('authenticated')} />;
  }

  return (
    <ExplainProvider value={settings.showExplanations}>
    <div class="shell">
      {/* Desktop-only (>= 1024px, see global.css) persistent sidebar — hidden entirely
          on mobile/tablet, where the app-bar + drawer below take over instead. Reads the
          SAME NAV_ITEMS array via NavButtons; nothing nav-related is duplicated. */}
      <aside class="sidebar" aria-label="Hauptnavigation">
        <div class="sidebar-brand">
          <span class="mark" aria-hidden="true" />
          ETF Cockpit
          <ExplainToggleButton active={settings.showExplanations} onToggle={toggleExplanations} />
        </div>
        <nav class="sidebar-nav">
          <NavButtons activeView={view} onSelect={go} />
        </nav>
      </aside>

      <div class="shell-content">
        <header class="app-bar">
          <button
            type="button"
            ref={burgerRef}
            class="burger-btn"
            aria-label="Menü öffnen"
            aria-haspopup="true"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <BurgerIcon />
          </button>
          <div class="app-title">
            <span class="mark" aria-hidden="true" />
            ETF Cockpit
          </div>
          <ExplainToggleButton active={settings.showExplanations} onToggle={toggleExplanations} />
        </header>

        <div
          class={`drawer-backdrop${drawerOpen ? ' open' : ''}`}
          onClick={closeDrawer}
          aria-hidden={!drawerOpen}
        />

        <div
          class={`drawer${drawerOpen ? ' open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Hauptnavigation"
          inert={drawerOpen ? undefined : true}
        >
          <div class="drawer-header">
            <div class="drawer-header-left">
              <div class="app-title">
                <span class="mark" aria-hidden="true" />
                ETF Cockpit
              </div>
              <ExplainToggleButton active={settings.showExplanations} onToggle={toggleExplanations} />
            </div>
            <button type="button" class="drawer-close-btn" aria-label="Menü schließen" onClick={closeDrawer}>
              <CloseIcon />
            </button>
          </div>
          <nav class="drawer-nav" aria-label="Hauptnavigation">
            <NavButtons activeView={view} onSelect={go} firstItemRef={firstNavRef} />
          </nav>
        </div>

        {pendingShare ? (
          <div class="app-main" style={{ paddingBottom: 0 }}>
            <div class="banner banner-info" role="status">
              {pendingShare.scenario.ok ? (
                <>
                  <strong>Geteiltes Szenario erkannt: {pendingShare.scenario.scenario.label}</strong>
                  <span>
                    Der Link enthält ein Szenario. Es wird nicht automatisch geladen — bitte
                    ein Ziel wählen (überschreibt das gewählte Szenario, oder als neues
                    Szenario laden).
                  </span>
                  <div class="btn-row" style={{ marginTop: '0.5rem' }}>
                    {scenarioState.scenarios.map((entry) => (
                      <button key={entry.id} type="button" class="btn" onClick={() => loadPendingShareInto(entry.id)}>
                        → {entry.scenario.label}
                      </button>
                    ))}
                    <button type="button" class="btn" onClick={loadPendingShareAsNew}>
                      Als neues Szenario
                    </button>
                    <button type="button" class="btn" onClick={dismissPendingShare}>
                      Verwerfen
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <strong>Geteilter Link konnte nicht gelesen werden</strong>
                  <span>{pendingShare.scenario.error}</span>
                  <div class="btn-row" style={{ marginTop: '0.5rem' }}>
                    <button type="button" class="btn" onClick={dismissPendingShare}>
                      Verwerfen
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}

        <main class="app-main">
          {view === 'cockpit' ? (
            <CockpitView
              scenarioState={scenarioState}
              results={results}
              onUpdateScenario={updateScenario}
              onSetActiveScenario={setActiveScenarioAction}
              onAddScenario={addScenarioAction}
              onRenameScenario={renameScenarioAction}
              onDuplicateScenario={duplicateScenarioAction}
              onDeleteScenario={removeScenarioAction}
              onResetScenario={resetScenarioAction}
              onRetryScenario={forceRecomputeScenario}
              onNavigate={go}
              realTerms={realTerms}
              onRealTermsChange={setRealTerms}
            />
          ) : view === 'vergleich' ? (
            <VergleichView
              scenarios={scenarioState.scenarios}
              results={results}
              onRetryScenario={forceRecomputeScenario}
              onEditScenario={(id) => {
                setActiveScenarioAction(id);
                go('cockpit');
              }}
              realTerms={realTerms}
              onRealTermsChange={setRealTerms}
            />
          ) : view === 'szenarien' ? (
            <SzenarienView
              scenarioState={scenarioState}
              results={results}
              onLoadScenario={updateScenario}
              onLoadScenarioAsNew={(scenario) => setScenarioState((s) => addScenarioFrom(s, scenario))}
            />
          ) : view === 'historie' ? (
            <HistorieView />
          ) : view === 'einstellungen' ? (
            <EinstellungenView
              settings={settings}
              onSettingsChange={setSettings}
              onLoggedOut={() => setAuth('login')}
            />
          ) : view === 'info' ? (
            <InfoView />
          ) : (
            <DatenView />
          )}
        </main>
      </div>
    </div>
    </ExplainProvider>
  );
}
